import type { WorkflowDefinition } from "../types";

/**
 * W21 — Time content updates to when AI recrawls.
 *
 * `shared`, because the cadence is a fact about the whole site. `explicit`,
 * because the point of the playbook is a change you publish and then verify:
 * reading it as already part-done told a team its updates had been picked up
 * when nothing had been published yet.
 *
 * The plugin step is the one exemption. "Is traffic arriving?" is a standing
 * fact with no timestamp behind it, so a site that already has the plugin
 * starts here with that step ticked.
 */
export const recrawlTimingWorkflow: WorkflowDefinition = {
	id: "recrawl-timing",
	wNumber: 21,
	title: "Time content updates to when AI recrawls",
	outcome:
		"Publish and measure on the cadence your site really has, not the one you assumed.",
	audiences: ["inhouse", "content"],
	uses: ["Analytics Setup", "Recrawl Frequency", "Annotations"],
	scope: "property",
	concurrency: "shared",
	progress: "explicit",
	isApplicable: (mode) => mode.showAnalytics,
	articleSlug: "how-to-time-content-updates-to-ai-recrawl-cadence",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "install-plugin",
			title: "Install the plugin so crawls get logged",
			page: "Analytics Setup",
			route: "/analytics",
			criterion: "You've installed the plugin and traffic is arriving.",
			why: "The cadence is measured from crawler visits to your server. Without that log there is no number to work from.",
			docsHref: "/docs/dashboards/ai-traffic-analytics",
			tasks: [
				"Install the plugin for your platform",
				"Give it two weeks before you trust the average",
			],
			complete: { kind: "flag", key: "isVerified" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Traffic arriving",
									done: state.property.isVerified,
								},
							],
						}
					: null,
		},
		{
			id: "read-cadence",
			title: "Read how often crawlers come back",
			page: "Recrawl Frequency",
			route: "/recrawl",
			criterion: "You've read the site-wide interval.",
			why: "Most teams assume same-day pickup. The real interval is usually weeks, which changes when you should measure.",
			docsHref: "/docs/dashboards/recrawl-frequency",
			tasks: [
				"Read the site-wide average interval",
				"Compare the crawlers that feed answers against the rest",
			],
			complete: { kind: "manual" },
		},
		{
			id: "starved-pages",
			title: "Find the pages AI rarely comes back to",
			page: "Recrawl Frequency",
			route: "/recrawl",
			criterion: "You've listed the pages that go untouched.",
			why: "An important page that goes months between visits will not reflect any edit you make. Linking and sitemaps come first there.",
			docsHref: "/docs/dashboards/recrawl-frequency",
			tasks: [
				"Sort pages by how long it has been since the last visit",
				"Flag the commercial pages sitting in that list",
				"Fix internal linking and sitemap coverage for them",
			],
			complete: { kind: "manual" },
		},
		{
			id: "publish-and-note",
			title: "Publish on the cadence and note the date",
			page: "Annotations",
			route: "/annotations",
			criterion: "You've noted an update since you started.",
			why: "The note is what lets you tell an update apart from noise weeks later. Nobody remembers the date by then.",
			docsHref: "/docs/dashboards/annotations",
			tasks: [
				"Line the publishing calendar up with the real interval",
				"Note the day each update goes live",
			],
			complete: { kind: "since", event: "annotationCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Updates noted",
							value: state.property.manualAnnotationCount,
						}
					: null,
		},
		{
			id: "verify-pickup",
			title: "Check the update was actually picked up",
			page: "Recrawl Frequency",
			route: "/recrawl",
			criterion: "You've confirmed the page was fetched again.",
			why: "A page that was never refetched cannot have changed anything. A missing fetch is the finding, not an empty result.",
			docsHref: "/docs/dashboards/recrawl-frequency",
			tasks: [
				"Check the changed page for a visit after your publish date",
				"Only then judge whether the update worked",
			],
			complete: { kind: "manual" },
		},
	],
};
